import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from './lib/api.js';
import Login from './pages/Login.jsx';

const SessionContext = createContext(null);

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function SessionProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null, app: null, error: null, mode: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { user, app } = await api.get('/session/me');
      setState({ loading: false, user, app, error: null, mode: null });
    } catch (error) {
      // 401 means "not signed in yet" - show the login form rather than an
      // error page. Anything else is a real configuration or account problem.
      let mode = null;
      try {
        mode = await api.get('/auth/mode');
      } catch {
        /* the mode endpoint is itself unreachable; fall through to the error */
      }
      setState({ loading: false, user: null, app: null, error, mode });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      window.location.assign('/');
    }
  }, []);

  if (state.loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
        <span className="spinner" /> Loading FSWeb...
      </div>
    );
  }

  if (!state.user) {
    const canUseForm = state.error?.status === 401 && state.mode?.form;
    if (canUseForm) return <Login mode={state.mode} onSignedIn={load} />;
    return <SignInProblem error={state.error} mode={state.mode} onRetry={load} />;
  }

  return (
    <SessionContext.Provider value={{ ...state, reload: load, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Shown when sign-in cannot proceed at all — a configuration or provisioning
 * problem rather than a wrong password.
 */
function SignInProblem({ error, mode, onRetry }) {
  const help = {
    unauthenticated:
      'The browser did not present a Windows identity, and this site is not configured to show a login form. Enable IIS Windows Authentication and disable Anonymous, or set AUTH_MODE=both.',
    not_provisioned:
      'Your account authenticated, but it is not linked to an FSWeb user record. An administrator needs to create it, or add a mapping via AUTH_USER_MAP.',
    account_disabled: 'Your FSWeb user record is marked inactive. An administrator can re-enable it in User Profiles.',
    outside_network:
      'This account may only sign in from the office network. Connect to the VPN, or ask an administrator to grant remote access.',
    no_client_scope:
      'Your account has no client access configured, so there is nothing it can show. An administrator can grant it in User Profiles.',
    forwarded_identity_rejected:
      'The server received a Windows identity from an untrusted address. Add the reverse-proxy address to AUTH_TRUSTED_PROXIES.',
    network: 'The FSWeb server did not respond. It may be restarting.',
  }[error?.code];

  return (
    <div style={{ maxWidth: 660, margin: '70px auto', padding: 24 }}>
      <h1 style={{ color: 'var(--navy)', fontSize: 20, marginTop: 0 }}>
        {mode?.company || 'FSWeb'} cannot sign you in
      </h1>
      <div className="msg error">{error?.message || 'Unknown error.'}</div>
      {help && <div className="msg info">{help}</div>}
      <button className="btn primary" onClick={onRetry}>Try again</button>
    </div>
  );
}

export default SessionContext;
